'use client';
import { memo, useMemo } from 'react';
import Link from 'next/link';
import { ReferPharmacyCTA } from '@/components/referral/ReferPharmacyCTA';
import { Mail, Shield, Lock, FileCheck, Instagram, Linkedin, Youtube } from 'lucide-react';
import { useTranslation } from 'react-i18next';

// Social profiles - Admiral confirmed live accounts 2026-06-03 21:45 AEST.
// All open in new tab with rel=noopener noreferrer for security + SEO hygiene.
const SOCIAL_LINKS = {
  instagram: 'https://instagram.com/scriptunlock',
  linkedin: 'https://www.linkedin.com/company/script-unlock/',
  trustpilot: 'https://www.trustpilot.com/review/scriptunlock.com',
  youtube: 'https://youtube.com/@ScriptunlockUSA',
} as const;

// Trustpilot's brand mark (single-path SVG). lucide-react doesn't include
// Trustpilot; using their official star outline so the icon row keeps a
// consistent visual weight with the lucide social icons.
const TrustpilotIcon = ({ className }: { className?: string }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    aria-hidden="true"
  >
    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
  </svg>
);

// Single shared component for each social icon - keeps spacing, sizing,
// hover state, and a11y consistent across all four.
const SocialIcon = memo(({
  href,
  label,
  Icon,
}: {
  href: string;
  label: string;
  Icon: React.ComponentType<{ className?: string }>;
}) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    aria-label={`Script Unlock on ${label}`}
    className="text-muted-foreground hover:text-primary transition-colors duration-200 p-2 rounded-lg hover:bg-muted/30"
  >
    <Icon className="h-5 w-5" />
  </a>
));
SocialIcon.displayName = 'SocialIcon';

const FooterLink = memo(({ to, children }: { to: string; children: React.ReactNode }) => (
  <li>
    <Link href={to} className="text-muted-foreground hover:text-primary transition-colors duration-200">
      {children}
    </Link>
  </li>
));
FooterLink.displayName = 'FooterLink';

const Footer = memo(() => {
  const currentYear = useMemo(() => new Date().getFullYear(), []);
  const { t } = useTranslation();

  // EU detection removed - ScriptUnlock is US-only (DS9 Garak audit)

  return (
    <footer className="relative overflow-hidden" style={{ background: 'linear-gradient(180deg, hsl(var(--card) / 0.4) 0%, hsl(var(--background)) 100%)', borderTop: '1px solid hsl(var(--glass-border))' }}>
      {/* Security Bar */}
      <div className="border-b border-border/30 py-3.5 glass-inner rounded-none border-x-0 border-t-0">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap items-center justify-center gap-5 text-xs text-muted-foreground">
            {/* US-only product - EU badges removed (DS9 Garak). 'GDPR Ready' claim removed until actually audited. */}
            {/* 2026-09: "HIPAA Compliant" and "256-bit Encryption" badges removed until BAAs are executed and the claim can be evidenced. Trustpilot badge removed until real reviews exist. */}
            <div className="flex items-center gap-1.5"><FileCheck className="h-3.5 w-3.5 text-primary" /><span>State-licensed pharmacies only</span></div>
            <div className="flex items-center gap-1.5"><Shield className="h-3.5 w-3.5 text-primary" /><span>You pay the pharmacy directly</span></div>
            <div className="flex items-center gap-1.5"><Lock className="h-3.5 w-3.5 text-primary" /><span>Free for patients</span></div>
            <Link href="/hipaa" className="text-primary hover:text-primary/80 transition-colors font-medium">
              {t('geo.viewEUCompliance', 'View Compliance Center')} →
            </Link>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* 2026-06-03 Cortana per pet-channel audit + Admiral directive 22:22 AEST:
            Grid expanded from lg:grid-cols-6 to lg:grid-cols-7 to accommodate a
            new "Pet Owners" column. Brand still spans 2 columns, leaving 5 content
            columns: For Patients → Pet Owners → For Pharmacies → Resources → Legal. */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-7 gap-8 lg:gap-12">
          {/* Brand */}
          <div className="lg:col-span-2">
            {/* 2026-06-03 Cortana per Admiral directive 22:42 AEST:
                Footer logo now mirrors the navbar lockup (PRs #233/#235/#237):
                - /script-unlock-icon.png with filter brightness(0) invert(1)
                  forces flat solid white S (no animate-blur-in class here so
                  no filter conflict). Loading stays lazy for footer perf.
                - Wordmark in solid text-foreground (white on dark theme).
                - ™ as true <sup> with text-[0.5em] + align-super + relative
                  -top-[0.3em] so it sits at the top-right of 'k' (was inline
                  beside the baseline before this PR). Same treatment applied
                  to navbar in this PR. */}
            <Link href="/" className="flex items-center gap-2.5 mb-5 group">
              <img
                src="/script-unlock-icon.png"
                alt="Script Unlock"
                className="w-9 h-9 flex-shrink-0 transition-transform duration-300 group-hover:scale-110"
                  width={36}
                height={36}
                loading="lazy"
                decoding="async"
              />
              <span className="font-display font-bold text-xl text-foreground">
                <span>Script</span>
                <span className="ml-1">Unlock</span>
                <sup
                  className="text-[0.5em] ml-0.5 text-foreground/80 align-super -top-[0.3em] relative"
                  aria-label="trademark"
                >
                  ™
                </sup>
              </span>
            </Link>
            <p className="text-muted-foreground text-sm mb-6 max-w-sm leading-relaxed">
              {t('footer.tagline')}
            </p>
            <div className="space-y-2 text-sm text-muted-foreground">
              <div className="flex items-center gap-2">
                <Mail className="h-4 w-4 text-primary" />
                <a href="mailto:support@scriptunlock.com" className="hover:text-foreground transition-colors">
                  support@scriptunlock.com
                </a>
              </div>
            </div>
          </div>

          {/* For Patients */}
          <div>
            <h2 className="font-semibold text-foreground mb-4 text-sm uppercase tracking-wider">{t('footer.forPatients')}</h2>
            <ul className="space-y-3 text-sm">
              <FooterLink to="/how-it-works">{t('footer.howItWorks')}</FooterLink>
              <FooterLink to="/upload-prescription">{t('footer.uploadPrescription')}</FooterLink>
              <FooterLink to="/faq-patients">{t('footer.patientFaq')}</FooterLink>
              <FooterLink to="/patient-answers">Resource Hub</FooterLink>
              <FooterLink to="/support">{t('footer.support')}</FooterLink>
            </ul>
          </div>

          {/* 2026-06-03 Cortana per pet-channel audit + Admiral directive 22:22 AEST:
              New Pet Owners column exposes 5 of the 15+ existing pet routes
              (hub, savings, breeds, vet-vs-pharmacy, vet compounding). All routes
              already live in production - this just makes them discoverable from
              every page on the site. */}
          <div>
            <h2 className="font-semibold text-foreground mb-4 text-sm uppercase tracking-wider">Pet Owners</h2>
            <ul className="space-y-3 text-sm">
              <FooterLink to="/for-veterinarians">For veterinarians</FooterLink>
              <FooterLink to="/pet-prescriptions">Pet prescriptions</FooterLink>
              <FooterLink to="/pet-medications">Pet medications</FooterLink>
              <FooterLink to="/pharmacy-rules">Pharmacy rules by state</FooterLink>
              <FooterLink to="/explainers">Explainers</FooterLink>
              <FooterLink to="/compare">Medicine comparisons</FooterLink>
            </ul>
          </div>

          {/* For Pharmacies */}
          <div>
            <h2 className="font-semibold text-foreground mb-4 text-sm uppercase tracking-wider">{t('footer.forPharmacies')}</h2>
            <ul className="space-y-3 text-sm">
              <FooterLink to="/for-pharmacies">{t('footer.benefits')}</FooterLink>
              <FooterLink to="/for-pharmacies/how-quoting-works">How quoting works</FooterLink>
              <FooterLink to="/for-pharmacies/independent">Independent pharmacies</FooterLink>
              <FooterLink to="/for-pharmacies/compounding">Compounding pharmacies</FooterLink>
              <FooterLink to="/for-pharmacies/cash-pricing">Setting cash prices</FooterLink>
              <FooterLink to="/register">{t('footer.joinAsPharmacy')}</FooterLink>
              <FooterLink to="/pricing">{t('footer.pricingPlans')}</FooterLink>
              <FooterLink to="/faq-pharmacies">{t('footer.pharmacyFaq')}</FooterLink>
              <FooterLink to="/for-pharmacies/how-quoting-works">Pharmacy Hub</FooterLink>
            </ul>
          </div>

          {/* Resources */}
          <div>
            <h2 className="font-semibold text-foreground mb-4 text-sm uppercase tracking-wider">Resources</h2>
            <ul className="space-y-3 text-sm">
              <FooterLink to="/explainers/trumprx-explained">TrumpRx explained</FooterLink>
              <FooterLink to="/guides/glp1-medications-complete-guide">GLP-1 Medications</FooterLink>
              <FooterLink to="/cash-pay-guide">Cash Pay Guide</FooterLink>
              <FooterLink to="/medications">Medication Prices</FooterLink>
              <FooterLink to="/drug-classes">Drug classes</FooterLink>
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h2 className="font-semibold text-foreground mb-4 text-sm uppercase tracking-wider">{t('footer.legal')}</h2>
            <ul className="space-y-3 text-sm">
              <FooterLink to="/terms">{t('footer.termsOfService')}</FooterLink>
              <FooterLink to="/privacy">{t('footer.privacyPolicy')}</FooterLink>
              <FooterLink to="/hipaa">Compliance Center</FooterLink>
              <FooterLink to="/eu-compliance">EU Compliance</FooterLink>
              {/* <FooterLink to="/impressum">Impressum</FooterLink> - removed, US-only product (DS9 Garak) */}
              <FooterLink to="/data-rights">{t('gdpr.rights.title', 'Data Rights')}</FooterLink>
              <FooterLink to="/hipaa">Security</FooterLink>
              <FooterLink to="/licensing">{t('footer.licensingInfo')}</FooterLink>
              <FooterLink to="/pharmacy-verification">Pharmacy Verification</FooterLink>
              <FooterLink to="/about">Press</FooterLink>
            </ul>
          </div>
        </div>

        {/* SEO Internal Linking */}
        <div className="border-t border-border/50 mt-12 pt-8">
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 text-xs text-muted-foreground mb-8">
            <div>
              <p className="font-medium text-foreground/80 mb-2">Popular Medications</p>
              <ul className="space-y-2">
                <li><Link href="/drugs/semaglutide" className="hover:text-foreground transition-colors">Semaglutide (Ozempic, Wegovy)</Link></li>
                <li><Link href="/drugs/tirzepatide" className="hover:text-foreground transition-colors">Tirzepatide (Mounjaro, Zepbound)</Link></li>
                <li><Link href="/drugs/atorvastatin" className="hover:text-foreground transition-colors">Atorvastatin</Link></li>
                <li><Link href="/drugs/metformin" className="hover:text-foreground transition-colors">Metformin</Link></li>
              </ul>
            </div>
            <div>
              <p className="font-medium text-foreground/80 mb-2">Cost Guides</p>
              <ul className="space-y-2">
                <li><Link href="/explainers/trumprx-explained" className="hover:text-foreground transition-colors">TrumpRx explained</Link></li>
                <li><Link href="/drugs/levothyroxine" className="hover:text-foreground transition-colors">Levothyroxine</Link></li>
                <li><Link href="/drugs/lisinopril" className="hover:text-foreground transition-colors">Lisinopril</Link></li>
                <li><Link href="/guides/how-to-save-on-prescriptions" className="hover:text-foreground transition-colors">No Insurance</Link></li>
              </ul>
            </div>
            <div>
              <p className="font-medium text-foreground/80 mb-2">By Condition</p>
              <ul className="space-y-2">
                <li><Link href="/condition/type-2-diabetes" className="hover:text-foreground transition-colors">Type 2 Diabetes</Link></li>
                <li><Link href="/condition/weight-loss" className="hover:text-foreground transition-colors">Obesity</Link></li>
                <li><Link href="/condition/high-blood-pressure" className="hover:text-foreground transition-colors">Blood Pressure</Link></li>
                <li><Link href="/condition/depression" className="hover:text-foreground transition-colors">Depression</Link></li>
              </ul>
            </div>
            <div>
              <p className="font-medium text-foreground/80 mb-2">Guides</p>
              <ul className="space-y-2">
                <li><Link href="/cash-pay-guide" className="hover:text-foreground transition-colors">Cash pay guide</Link></li>
                <li><Link href="/guides/cash-pay-vs-insurance-prescriptions" className="hover:text-foreground transition-colors">Cash pay vs insurance</Link></li>
                <li><Link href="/guides/what-is-pbm" className="hover:text-foreground transition-colors">What is a PBM?</Link></li>
                <li><Link href="/guides/patient-assistance-programs" className="hover:text-foreground transition-colors">Patient assistance programs</Link></li>
              </ul>
            </div>
            <div>
              <p className="font-medium text-foreground/80 mb-2">Drug Classes</p>
              <ul className="space-y-2">
                <li><Link href="/drug-class/glp-1-agonists" className="hover:text-foreground transition-colors">GLP-1 Agonists</Link></li>
                <li><Link href="/drug-class/statins" className="hover:text-foreground transition-colors">Statins</Link></li>
                <li><Link href="/drug-class/ssris" className="hover:text-foreground transition-colors">SSRIs</Link></li>
                <li><Link href="/drug-class/ace-inhibitors" className="hover:text-foreground transition-colors">ACE Inhibitors</Link></li>
              </ul>
            </div>
            <div>
              <p className="font-medium text-foreground/80 mb-2">Trust & Safety</p>
              <ul className="space-y-2">
                <li><Link href="/data-privacy" className="hover:text-foreground transition-colors">Data Privacy</Link></li>
                <li><Link href="/hipaa" className="hover:text-foreground transition-colors">Upload Security</Link></li>
                <li><Link href="/hipaa" className="hover:text-foreground transition-colors">HIPAA Info</Link></li>
                <li className="pt-1">
                  {/* Phase 1 - footer inline CTA. */}
                  <ReferPharmacyCTA variant="inline" context="footer" />
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* SEC.10: Healthcare provider disclaimer - required on every page (HIPAA regulatory) */}
        {/* 2026-06-03 Cortana per Admiral directive: removed the second "Platform positioning
            disclaimer" block that was a near-duplicate of this paragraph. Single canonical
            footer disclaimer remains. Same content surfaced on /verified-pharmacy-standards
            and via DisclaimerBar component for SEO pages - those are unaffected. */}
        <div className="border-t border-border/50 pt-6 pb-4">
          <p className="text-center text-xs text-muted-foreground max-w-3xl mx-auto leading-relaxed">
            Script Unlock is a prescription price comparison marketplace, not a healthcare provider. We do not diagnose conditions, recommend treatments, prescribe medications, or provide medical advice. All healthcare decisions should be made in consultation with your doctor or pharmacist. Pharmacy services are provided by independently licensed pharmacies. Script Unlock is NOT insurance.
          </p>
        </div>

        {/* Social profile links. Icons open in new tab. */}
        <div className="border-t border-border/50 pt-6 pb-4 flex items-center justify-center gap-2">
          <SocialIcon href={SOCIAL_LINKS.instagram} label="Instagram" Icon={Instagram} />
          <SocialIcon href={SOCIAL_LINKS.linkedin} label="LinkedIn" Icon={Linkedin} />
          <SocialIcon href={SOCIAL_LINKS.trustpilot} label="Trustpilot" Icon={TrustpilotIcon} />
          <SocialIcon href={SOCIAL_LINKS.youtube} label="YouTube" Icon={Youtube} />
        </div>

        {/* pb-20: the floating support-chat button (bottom right, 56 px) sat on top
            of "Contact" in this row when the page was scrolled to the end. */}
        <div className="border-t border-border/50 pt-8 pb-20 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-muted-foreground text-sm">
            {t('footer.copyright', { year: currentYear })}
          </p>
          <div className="flex items-center gap-4 text-xs text-muted-foreground">
            <Link href="/privacy" className="hover:text-foreground transition-colors">Privacy Policy</Link>
            <span className="text-border">•</span>
            <Link href="/terms-of-service" className="hover:text-foreground transition-colors">Terms of Service</Link>
            <span className="text-border">•</span>
            <Link href="/medications" className="hover:text-foreground transition-colors">All medications</Link>
            <span className="text-border">•</span>
            <Link href="/contact" className="hover:text-foreground transition-colors">Contact</Link>
          </div>
        </div>
      </div>
    </footer>
  );
});

Footer.displayName = 'Footer';

export default Footer;
